import { DateValue } from "@heroui/react";
import { getLocalTimeZone, parseDate } from "@internationalized/date";

/**
 * Convert a DateValue to an ISO string with the user's timezone
 * @param dateValue The DateValue to convert
 * @returns ISO string representation in the user's timezone
 */
export function dateValueToISOString(dateValue: DateValue): string {
  return dateValue.toDate(getLocalTimeZone()).toISOString();
}

/**
 * Convert an ISO string to a DateValue
 * @param isoString The ISO string to convert
 * @returns DateValue representation
 */
export function isoStringToDateValue(isoString: string): DateValue {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return parseDate(`${year}-${month}-${day}`);
}

/**
 * Convert a Date to a human-readable due date string
 * @param date The due date to convert
 * @returns String representation like "due today", "due in 2 days", "overdue by 1 day", etc.
 */
export function formatDueDate(date: Date): string {
  const today = new Date();

  // Reset time components to compare only dates
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const targetDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffTime = targetDateOnly.getTime() - todayDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "due today";
  } else if (diffDays === 1) {
    return "due tomorrow";
  } else if (diffDays > 1) {
    return `due in ${diffDays} days`;
  } else if (diffDays === -1) {
    return "overdue by 1 day";
  } else {
    return `overdue by ${Math.abs(diffDays)} days`;
  }
}

/**
 * Displays remaining days until subscription expires
 * @param expirationDate The expiration date of the subscription
 * @returns String representation of remaining days
 */
export function formatSubscriptionExpiry(expirationDate: Date): string {
  const today = new Date();
  const diffTime = expirationDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "today";
  } else if (diffDays === 1) {
    return "tomorrow";
  } else if (diffDays > 1) {
    return `in ${diffDays} days`;
  } else {
    return `Expired ${Math.abs(diffDays)} days ago`;
  }
}

/**
 * Format a date to "DD MonthName Year" format
 * @param date The date to format
 * @returns String representation like "30 November 2025"
 */
export function formatCompletedDate(date: Date | string): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const day = dateObj.getDate();
  const month = dateObj.toLocaleDateString("en-US", { month: "short" });
  const year = dateObj.getFullYear();
  return `${day} ${month} ${year}`;
}
